/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import "react-datepicker/dist/react-datepicker.css";
import colors from "../assets/colors.json";
import Filter from "../components/Filter";
import { ResponsiveBar } from "@nivo/bar";
import fetchHelper from "../services/fetchHelper";
import { LayoutContext } from "../components/layout";

let m2d;

export const queryDateFormat = (date) => {
  let y = date.getFullYear();
  let m = (1 + date.getMonth()).toString().padStart(2, "0");
  let d = date.getDate().toString().padStart(2, "0");

  return [y, m, d].join("-");
};

const UnsignedStatBar = (prp) => {
  const { where, onLoadingChange } = prp;
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [minValue, setMinValue] = useState(4);

  let localKeys = [];
  const setupData = (rawData) => {
    onLoadingChange(false);
    let minValue = 0;
    const data = rawData.map((item) => {
      item[`${item["physician"]}-POC`] = item.pocUnsigned * -1;
      item[`${item["physician"]}-REMOTE`] = item.remoteUnsigned;
      localKeys = [...localKeys, `${item["physician"]}-POC`, `${item["physician"]}-REMOTE`];
      return item;
    });

    setKeys([...new Set(localKeys)]);

    console.log("DATA", JSON.stringify(data));

    data.reverse();

    const mappedData = [];
    data.forEach((i) => {
      let index = mappedData.findIndex((e) => e.month === i.month);
      if (index == -1) mappedData.push(i);
      else mappedData[index] = { ...mappedData[index], ...i };
    });

    setData(mappedData);
    minValue = Math.ceil(minValue / 100) * 100;
    setMinValue((minValue + 100) * -1);
  };

  const poll = async () => {
    try {
      if (!where) return false;
      const urlParams = {
        where: where,
      };
      const lS = window.localStorage.getItem("genToken");
      let url = "/patientmanagement/rest/interrogations/unsignedCannedReport?";
      // if (process.env.NODE_ENV === "development") url = "/reportStatementViewReport.json?";
      onLoadingChange(true);
      const j = await fetchHelper.get({ url: url + new URLSearchParams(urlParams) });
      setupData(j);
    } catch (e) {
      onLoadingChange(false);
      console.log(e);
    }
  };

  useEffect(() => {
    let t = JSON.stringify(data);
    if (t != m2d) m2d = t;
  }, [data]);

  useEffect(() => {
    if (where) {
      poll();
    }
  }, [where]);

  useEffect(() => {
    console.log("KEYS", keys);
  }, [keys]);

  return (
    <div className="dashboard-bar-container">
      <div className="dashboard-bar-content-container" style={{ height: 800 }}>
        <ResponsiveBar
          data={data}
          keys={keys.reverse()}
          indexBy="month"
          margin={{ top: 50, right: 220, bottom: 90, left: 60 }}
          padding={0.3}
          colors={{ scheme: "nivo" }}
          // colors={(d) => {
          //   const cM = Object.fromEntries(keys.map((_, i) => [keys[i], colors[i]]));
          //   console.log("cM", cM);
          //   // let cM = { PACE: "FFEFBE", ILR: "F1E0AB", ICD: "DDBE8F", CRTP: "4093A3", CRTD: "57BCCD", BIO: "81D6E4", BOSTON: "45A334", JUDE: "81D2E5", MED: "F1D6F4" };
          //   return cM[d.id];
          // }}
          markers={[
            {
              axis: "y",
              value: 0,
              lineStyle: { strokeOpacity: 0.1, stroke: "#fff", strokeWidth: 10 },
              textStyle: { fill: "#008c98" },
              legend: "Remote",
              legendPosition: "top-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
            },
            {
              axis: "y",
              value: 0,
              lineStyle: { stroke: "#fff", strokeWidth: 2 },
              textStyle: { fill: "#EE5D74" },
              legend: "POC",
              legendPosition: "bottom-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
            },
          ]}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          labelFormat={(label) => {
            return (label + "").replace("-", "");
          }}
          fill={[
            { match: { id: "fries" }, id: "dots" },
            { match: { id: "sandwich" }, id: "lines" },
          ]}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={{
            tickSize: 0,
            format: () => null,
            tickPadding: -50,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
          }}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -90,
            legend: "Month",
            legendPosition: "middle",
            legendOffset: 80,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -40,
            format: (v) => (v + "").replace("-", ""),
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={(data) => {
            const cM = { "30 Day": "fff", "90 Day": "303030", "30&90 Day": "606060" };
            return "#" + cM[data.data.id];
          }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "bottom-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [{ on: "hover", style: { itemOpacity: 1 } }],
            },
          ]}
          // tooltip={(tooltip) => {
          //   return (
          //     <div style={{ display: "flex", flexDirection: "column" }}>
          //       <span>
          //         {tooltip.id === "unique" ? "Patients" : tooltip.id}: {(tooltip.value + "").replace("-", "")}
          //       </span>
          //       {tooltip.value < 0 ? <span>Total Statements: {tooltip.data.view}</span> : null}
          //       {tooltip.value < 0 && tooltip.data.uniqueInterrogation ? <span>Reports: {tooltip.data.uniqueInterrogation}</span> : null}
          //     </div>
          //   );
          // }}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
        />
        <div className="bar-label-container">
          <span>Unsigned Reports Stats</span>
        </div>
      </div>
    </div>
  );
};

const UnsignedStat = () => {
  const { user } = useContext(LayoutContext);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (user) {
      setLocations([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
    }
  }, [user]);

  const onChangeFilter = (filter) => {
    if (locations.length > 0) setFilter(filter);
  };

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <Filter
        key={locations.length + "filter"}
        onChange={onChangeFilter}
        loading={loading}
        fields={[
          { type: "line" },
          {
            type: "multiSelect",
            whereKey: "location.id",
            key: "location",
            operator: "$eq",
            label: "Location",
            options: locations,
            maxSelect: 1,
            forceHasSelectAll: false,
            initialSelected: [{ value: user?.locations[0]?.id }],
          },
        ]}
      />
      <UnsignedStatBar where={filter.where} onLoadingChange={(v) => setLoading(v)} />
    </div>
  );
};

export default UnsignedStat;
